import React, { useState, useCallback } from "react";
import { Button, Toast, ToastProps, ToastContent } from "flux";
import ToastContext, { ToastAction } from "./toast-context";
import { Z_TOAST } from "../constants/z-index";

interface ToastState {
  id: number;
  message: string;
  action?: ToastAction;
  options?: ToastProps;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

function ToastProvider({ children }: ToastProviderProps): React.JSX.Element {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const openToast = useCallback((message: string, action?: ToastAction, options: ToastProps = {}) => {
    const newToast: ToastState = {
      id: Date.now(),
      message,
      action,
      options: { ...options, autoHideDuration: 4000 }
    };
    setToasts((prevToasts) => [...prevToasts, newToast]);
  }, []);

  const handleClose = useCallback((id: number) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  const handleAction = useCallback(
    async (id: number, actionCallback: () => Promise<void>) => {
      await actionCallback();
      handleClose(id);
    },
    [handleClose]
  );

  const ToastContentElement = ToastContent as React.ComponentType<Parameters<typeof ToastContent>[0]>;

  return (
    <ToastContext.Provider value={{ openToast }}>
      {children}
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{
            position: "fixed",
            bottom: `${8 + index * 56}px`, // 8px initial bottom spacing, 56px for each toast (adjust as needed)
            left: "16px",
            zIndex: Z_TOAST,
            pointerEvents: "none" // This allows clicks to pass through to elements underneath
          }}
        >
          <Toast
            key={toast.id}
            open={true}
            onClose={() => handleClose(toast.id)}
            ClickAwayListenerProps={{ mouseEvent: false }}
            {...toast.options}
            sx={{ position: "static", pointerEvents: "auto" }} // This ensures the toast itself is clickable
          >
            <ToastContentElement
              sx={(theme) => ({
                backgroundColor: theme.palette.info.main // TODO: Should this be coming from a prop?
              })}
              message={toast.message}
              action={
                <>
                  {toast.action && (
                    <Button
                      color="default"
                      size="small"
                      variant="text"
                      onClick={() => handleAction(toast.id, toast.action!.onClick)}
                      sx={{
                        textDecoration: "underline",
                        pr: "40px",
                        "&:hover": {
                          textDecoration: "underline"
                        }
                      }}
                      dataDogIdentifier="toast action"
                    >
                      {toast.action.label}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="default"
                    size="small"
                    onClick={() => handleClose(toast.id)}
                    dataDogIdentifier="toast dismiss"
                  >
                    Dismiss
                  </Button>
                </>
              }
            />
          </Toast>
        </div>
      ))}
    </ToastContext.Provider>
  );
}

export default ToastProvider;
